// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import { getReferenceString } from '@medplum/core';
import type { Extension, Patient, Practitioner } from '@medplum/fhirtypes';
import type { PatientTreatment } from '@polarissleep/shared';
import { PATIENT_EXTENSION_URLS } from '../constants/patient-profile';

export const TREATMENT_EXTENSION_URLS = {
  modality: 'modality',
  start: 'start',
  end: 'end',
  other: 'other',
  addedBy: 'added-by',
  endedBy: 'ended-by',
  source: 'source',
  orderId: 'order-id',
  orderName: 'order-name',
} as const;

export type { PatientTreatment };

function value(extension: Extension[] | undefined, url: string): string | undefined {
  return (
    extension?.find((item) => item.url === url)?.valueString ?? extension?.find((item) => item.url === url)?.valueDate
  );
}

export function getPatientTreatments(patient: Patient): PatientTreatment[] {
  return (patient.extension ?? [])
    .filter((extension) => extension.url === PATIENT_EXTENSION_URLS.treatment)
    .map((extension, index) => {
      const nested = extension.extension;
      return {
        id: `manual-${index}`,
        modality: value(nested, TREATMENT_EXTENSION_URLS.modality) ?? 'Other',
        start: value(nested, TREATMENT_EXTENSION_URLS.start) ?? '',
        end: value(nested, TREATMENT_EXTENSION_URLS.end),
        other: value(nested, TREATMENT_EXTENSION_URLS.other),
        addedBy: value(nested, TREATMENT_EXTENSION_URLS.addedBy),
        endedBy: value(nested, TREATMENT_EXTENSION_URLS.endedBy),
        source: value(nested, TREATMENT_EXTENSION_URLS.source) as PatientTreatment['source'],
        orderId: value(nested, TREATMENT_EXTENSION_URLS.orderId),
        orderName: value(nested, TREATMENT_EXTENSION_URLS.orderName),
      };
    })
    .filter((treatment) => treatment.start);
}

/** Local calendar date as YYYY-MM-DD, matching the date-only values treatments store. */
export function localDateString(date: Date = new Date()): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** @deprecated Treatments are stored as CarePlans; the Patient extension is read only by the backfill and fallback. */
export function setPatientTreatments(
  patient: Patient,
  treatments: PatientTreatment[],
  practitioner?: Practitioner
): Patient {
  const existing = (patient.extension ?? []).filter((extension) => extension.url !== PATIENT_EXTENSION_URLS.treatment);
  const treatmentExtensions = treatments.map((treatment) => ({
    url: PATIENT_EXTENSION_URLS.treatment,
    extension: [
      { url: TREATMENT_EXTENSION_URLS.modality, valueString: treatment.modality },
      { url: TREATMENT_EXTENSION_URLS.start, valueDate: treatment.start },
      ...(treatment.end ? [{ url: TREATMENT_EXTENSION_URLS.end, valueDate: treatment.end }] : []),
      ...(treatment.other ? [{ url: TREATMENT_EXTENSION_URLS.other, valueString: treatment.other }] : []),
      ...(treatment.addedBy || practitioner?.id
        ? [
            {
              url: TREATMENT_EXTENSION_URLS.addedBy,
              valueString: treatment.addedBy ?? (practitioner ? getReferenceString(practitioner) : undefined),
            },
          ]
        : []),
      ...(treatment.endedBy ? [{ url: TREATMENT_EXTENSION_URLS.endedBy, valueString: treatment.endedBy }] : []),
      ...(treatment.source ? [{ url: TREATMENT_EXTENSION_URLS.source, valueString: treatment.source }] : []),
      ...(treatment.orderId ? [{ url: TREATMENT_EXTENSION_URLS.orderId, valueString: treatment.orderId }] : []),
      ...(treatment.orderName ? [{ url: TREATMENT_EXTENSION_URLS.orderName, valueString: treatment.orderName }] : []),
    ],
  }));
  return { ...patient, extension: [...existing, ...treatmentExtensions] };
}
